'use client'
import * as React from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useUserIsDesigner } from '@repo/cf-user/hooks/use-user-data'
import { Link } from '@repo/design-system-cf/components/link'

import { tw } from 'utils/classnames'

import { useModalContext } from '../_context/modal-context'
import { Card } from './card'
import { Button } from '@web/components/prod/button/button'
import { ExternalLinkAltIcon } from '@web/components/icons/external-link-alt'
import { WarningIcon } from '@web/components/icons/warning'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { calculateDesignerVerificationCooldown } from '@web/utils/designer-verification-utils'
import { BanStatus } from 'codegen/types'
import {
  useGetDesignerVerificationStatusQuery,
  useUsersBanStatusForCurrentUserQuery,
} from '@web/gen/user'

export const DesignerBanner = () => {
  const locale = useLocale()
  const { openOpenStoreModal } = useModalContext()
  const { data: isBanned, isLoading: isBannedLoading } = useUsersBanStatusForCurrentUserQuery(
    undefined,
    {
      select: data =>
        [BanStatus.UserBanStatusDesignerBlock].includes(
          data?.usersBanStatusForCurrentUser?.banStatus,
        ) || false,
    },
  )
  const { data: designerVerificationStatus, isLoading: isDesignerVerificationStatusLoading } =
    useGetDesignerVerificationStatusQuery(undefined, {
      select: data => {
        if (!data) {
          return null
        }
        const { status, requestDate } = data.getDesignerVerificationStatus
        return { status, requestDate }
      },
    })
  const isLoading = isBannedLoading || isDesignerVerificationStatusLoading

  const t = useTranslations('my_account.designer_banner')
  const isDesigner = useUserIsDesigner()

  const { daysRemaining, cooldownPassed, isRejected, isPending } =
    calculateDesignerVerificationCooldown(designerVerificationStatus)

  const title = React.useMemo(() => {
    if (isRejected && !cooldownPassed) {
      return (
        <div className="flex items-center gap-3">
          <WarningIcon className="size-5 fill-cf-red-800" />
          {t('rejected_title')}
        </div>
      )
    }

    if (isPending) {
      return t('pending_title')
    }

    return t('title')
  }, [isRejected, cooldownPassed, isPending, t])

  const message = React.useMemo(() => {
    if (isRejected && !cooldownPassed) {
      return (
        <div>
          <p className="text-gray-600">{t('rejected_message')}</p>
          <p className="text-gray-600">
            {t.rich('rejected_message_2', {
              days: daysRemaining,
              email: str => (
                <Link
                  asChild={false}
                  variant={'primary-green'}
                  href={'mailto:hi@creativefabrica.com'}
                  target="_blank"
                >
                  {str}
                </Link>
              ),
            })}
          </p>
        </div>
      )
    }

    if (isPending) {
      return (
        <p className="text-gray-600">
          {t('pending_message')}{' '}
          <Link
            variant={'primary-green'}
            href={addOrReplaceLocaleInUrl(AppRoutes.OPEN_STORE, locale)}
            target="_blank"
            icon={<ExternalLinkAltIcon className="fill-cf-theme-green-400" />}
          >
            {t('learn_more')}
          </Link>
        </p>
      )
    }

    return (
      <p className="text-gray-600">
        {t('copy')}{' '}
        <Link
          variant={'primary-green'}
          href={addOrReplaceLocaleInUrl(AppRoutes.OPEN_STORE, locale)}
          target="_blank"
          icon={<ExternalLinkAltIcon className="fill-cf-theme-green-400" />}
        >
          {t('learn_more')}
        </Link>
      </p>
    )
  }, [isRejected, cooldownPassed, isPending, t, daysRemaining, locale])

  const cta = React.useMemo(() => {
    if (isRejected && !cooldownPassed) {
      return null
    }

    return (
      <div>
        <Button
          onClick={openOpenStoreModal}
          variant="normal"
          className={tw('px-3', {
            'text-cf-grey-400': isPending,
          })}
          disabled={isPending}
        >
          <span>{isPending ? t('pending_cta') : t('cta')}</span>
        </Button>
      </div>
    )
  }, [isRejected, cooldownPassed, isPending, openOpenStoreModal, t])

  if (isDesigner || isLoading || isBanned) {
    return null
  }

  return (
    <Card className="flex flex-col gap-4">
      <h2 className="text-20 font-bold">{title}</h2>
      {message}
      {cta}
    </Card>
  )
}
